import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { getDashboardPreferences, getLoggedinPartnersInfo, resetDashboardPreferences } from "../../api/pfs";
import type { IErrorType } from "../../constants/apiconstants-hydra";
import type { IPartnerInfo } from "../../constants/apiconstants-pfs";
import { usePartnershipStatus } from "../../queries/partnershipStatus";
import Env from "../../utils/env";
import DraggableDashboard from "./DraggableDashboard";
import { UnauthenticatedDashboard } from "./UnauthenticatedDashboard";
import "./DraggableDashboard.scss";
import { Alert, Button, Flex, FlexItem } from "@patternfly/react-core";
import { UndoIcon } from "@patternfly/react-icons";
import { RHCErrorPage } from "@rhc-shared-components/error-page";
import { getMarketingBlocks } from "../../api/drupal";
import { IPlacementKeyTypes } from "../../constants/apiconstants-drupal";
import type { MKTContent } from "../../constants/constants";
import DashboardHeader from "./DashboardHeader/DashboardHeader";
import { MarketingAlertComponent } from "./MarketingAlertComponent";

const DashBoardWrapper: React.FC = () => {
	const { error: partnershipStatusError, isFetching, data: partnershipStatus } = usePartnershipStatus();

	const { error: partnerInfoError } = useQuery<IPartnerInfo, IErrorType>({
		queryKey: ["partnerInfo"],
		queryFn: async ({ signal }) => await getLoggedinPartnersInfo(true, signal),
		enabled: !!partnershipStatus?.isPartner,
	});

	useQuery<MKTContent>({
		queryKey: ["marketingBlockData"],
		queryFn: async ({ signal }) => {
			const response = await getMarketingBlocks(signal);
			const alert =
				response?.data?.placements?.find((elm) => elm?.placement_key === IPlacementKeyTypes?.alert)?.blocks?.[0] ||
				null;
			const webinar =
				response?.data?.placements?.find((elm) => elm?.placement_key === IPlacementKeyTypes?.learning)?.blocks?.[0] ||
				null;
			return { alert, webinar };
		},
	});

	const queryClient = useQueryClient();
	const { data: preferences } = useQuery({
		queryKey: ["dashboardPreferences"],
		queryFn: ({ signal }) => getDashboardPreferences(signal),
		enabled: !!partnershipStatus?.isPartner,
	});

	const resetMutation = useMutation({
		mutationFn: () => resetDashboardPreferences(),
		onSuccess: (data) => {
			queryClient.setQueryData(["dashboardPreferences"], { ...data });
			queryClient.setQueryData(["cardOrder"], null);
		},
	});

	const showEmployeeAlert =
		(window as any)?.sessionjs?.tokenParsed?.email_verified &&
		(window as any)?.sessionjs?.tokenParsed?.email.includes("@redhat.com");

	if (isFetching) return null;

	const error = partnerInfoError || partnershipStatusError;
	if (error) return <RHCErrorPage errorStatus={error?.status} />;

	return (
		<div>
			<DashboardHeader />
			{partnershipStatus?.isPartner && (
				<div className="dashboard-toolbar">
					<Flex justifyContent={{ default: "justifyContentFlexEnd" }} alignItems={{ default: "alignItemsCenter" }}>
						<FlexItem>
							<Button
								variant="link"
								icon={<UndoIcon />}
								onClick={() => resetMutation.mutate()}
								isDisabled={preferences?.isDefault !== false || resetMutation.isPending}
							>
								Reset to default
							</Button>
						</FlexItem>
					</Flex>
				</div>
			)}
			{partnershipStatus?.isPartner && (
				<div style={{ padding: "0.5rem 3% 0" }}>
					<MarketingAlertComponent />
				</div>
			)}
			{partnershipStatus?.isPartner && showEmployeeAlert && (
				<Alert
					component="h2"
					title="This page shows all available partner capabilities. As an internal Red Hat user, your account may not be enabled for everything linked from this page."
					variant="warning"
					isInline
					style={{ margin: "0.5rem 3% 0" }}
				/>
			)}
			<div style={{ padding: "3%", paddingTop: partnershipStatus?.isPartner ? "1rem" : "3%" }}>
				<Switch>
					{partnershipStatus?.isPartner && (
						<Redirect exact={true} from={`${Env.routePrefix}/not-a-partner`} to={`${Env.routePrefix}/landing`} />
					)}
					{!partnershipStatus?.isPartner && (
						<Redirect exact={true} from={`${Env.routePrefix}/landing`} to={`${Env.routePrefix}/not-a-partner`} />
					)}
					{!partnershipStatus?.isPartner && (
						<Route exact={true} path={`${Env.routePrefix}/not-a-partner`} component={UnauthenticatedDashboard} />
					)}
					{partnershipStatus?.isPartner && (
						<Route exact={true} path={`${Env.routePrefix}/landing`} component={DraggableDashboard} />
					)}
				</Switch>
			</div>
		</div>
	);
};

export default DashBoardWrapper;
